import { useMemo } from 'react';
import {
  QCPatientVisitDates,
  QCTaskPatientVisit,
  QCVisitExtendedWithContextData,
} from '@dynamika-shared/types/visits';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { useQCPatientVisitsDatesSlice } from '@restore/QCPatientVisitsDates/QCPatientVisitsDates.slice';

export type QCVisitExtendedWithContextDataAndDates = QCPatientVisitDates &
  QCVisitExtendedWithContextData & {
    customOrder?: number;
    isDirty?: boolean;
  };

export const sortVisits = (
  a: QCVisitExtendedWithContextDataAndDates,
  b: QCVisitExtendedWithContextDataAndDates
): number => {
  const aOrder = a.customOrder ?? a.visitOrder ?? Infinity;
  const bOrder = b.customOrder ?? b.visitOrder ?? Infinity;
  return aOrder - bOrder;
};

export const sortVisitsByLockStatus = (
  visits: QCVisitExtendedWithContextDataAndDates[]
): QCVisitExtendedWithContextDataAndDates[] => {
  const lockedVisits = visits.filter(visit => visit.visitOrderLocked);
  const unlockedVisits = visits.filter(visit => !visit.visitOrderLocked);

  const sortedLockedVisits = lockedVisits.sort(sortVisits);
  const sortedUnlockedVisits = unlockedVisits.sort(sortVisits);

  return [...sortedLockedVisits, ...sortedUnlockedVisits];
};

export const useMergedVisits = (
  patientVisits?: QCTaskPatientVisit[]
): QCVisitExtendedWithContextDataAndDates[] => {
  const flatVisits = useClinicalTrialContextSlice(state => state.flatVisits);
  const [orderedVisits] = useQCPatientVisitsDatesSlice((state, actions) => [
    state.data ?? [],
    actions.fetchQCPatientVisitsDates,
  ]);

  return useMemo(() => {
    if (!patientVisits) return [];

    const pvById = new Map<string, QCTaskPatientVisit>(
      patientVisits.map(v => [v.id, v])
    );

    const combined = orderedVisits.reduce<
      QCVisitExtendedWithContextDataAndDates[]
    >((acc, item) => {
      const id = item.id;

      const pv: QCTaskPatientVisit | undefined = pvById.get(id);
      const ov: QCPatientVisitDates | undefined = item;

      const visitDefinitionId = pv?.visitDefinitionId ?? ov?.visitDefinitionId;
      const contextData = visitDefinitionId
        ? flatVisits[visitDefinitionId]
        : undefined;

      acc.push({
        ...(pv ?? {}),
        ...(contextData ?? {}),
        ...(ov ?? {}),
      } as QCVisitExtendedWithContextDataAndDates);
      return acc;
    }, []);

    const sortedVisits = sortVisitsByLockStatus(combined);

    return sortedVisits.map((visit, index) => ({
      ...visit,
      customOrder: index + 1,
      isDirty: false,
    }));
  }, [patientVisits, flatVisits, orderedVisits]);
};
